import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';

import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, startWith, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { FormSelectOption } from '../../../../form-controls/entities/form-select-option.entity';
import { MultiFormSelectOptions } from '../../../../form-controls/entities/multi-form-select-options.entity';
import { MultiFormSelectStyle } from '../../../../form-controls/entities/multi-form-select-style.entity';
import { BaseFilterDirective } from '../../base/components/base-filter/base-filter.component';
import {
    FilterMultiSelectTranslations,
    FilterMultiSelectTranslationsPartial
} from '../../base/entities/filter-multiselect-translations';
import { QueryPartStrategy } from '../../base/entities/query-part-strategy';
import { MultiSelectFilter } from './filter-multiselect.class';
import { ShFormMultiSelectBaseComponent } from '../../../../form-controls/components/form-multi-select/base-simple/form-multi-select-base.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'sh-filter-multiselect',
    templateUrl: './filter-multiselect.component.html',
    standalone: true,
    imports: [ShFormMultiSelectBaseComponent, ReactiveFormsModule, AsyncPipe]
})
export class ShFilterMultiSelectComponent extends BaseFilterDirective<MultiSelectFilter> implements OnInit {
    @Input() set i18n(customTranslations: FilterMultiSelectTranslationsPartial) {
        this.filterLabels = {
            ...this.defaultTranslations,
            ...customTranslations
        };
    }

    private readonly defaultTranslations: FilterMultiSelectTranslations = {
        loading: '',
        multipleSelected: '',
        allSelected: '',
        noneSelected: '',
        listCapped: ''
    };

    public filterLabels: FilterMultiSelectTranslations = this.defaultTranslations;
    public prefix$!: Observable<string>;
    public id$!: Observable<string>;
    public options$!: Observable<MultiFormSelectOptions>;
    public form: UntypedFormControl = new UntypedFormControl();
    public searchTerm$: Subject<string> = new Subject<string>();
    public style$!: Observable<MultiFormSelectStyle>;

    constructor() {
        super();
        this.initDataStreams();
    }

    public ngOnInit(): void {
        this.initFormListeners();
        this.initDefaultValuesListener();
    }

    private initFormListeners(): void {
        const stream$: Observable<string[]> = this.form.valueChanges;

        stream$
            .pipe(
                filter((vals): vals is string[] => vals.every(val => typeof val === 'string')),
                takeUntil(this.destroy$)
            )
            .subscribe(this.emitValues.bind(this));
    }

    private initDefaultValuesListener(): void {
        this.pureFilterStream$
            .pipe(takeUntil(this.destroy$))
            .subscribe(multiSelectFilter => this.form.setValue(multiSelectFilter.values));
    }

    private emitValues(values: string[]): void {
        const multiSelectFilter: MultiSelectFilter | undefined = this.rawFilterInput$.value;

        if (multiSelectFilter) {
            this.emitChange({ ...multiSelectFilter.filterData, values });
        }
    }

    private initDataStreams(): void {
        this.initOptionsStream();
        this.initPrefixStream();
        this.initStyleStream();
        this.initIdStream();
    }

    private initOptionsStream(): void {
        const searchOptions$: Observable<FormSelectOption[]> = this.searchTerm$.pipe(
            startWith(''),
            switchMap(term => this.search$(term))
        );

        this.options$ = combineLatest([searchOptions$, this.pureFilterStream$]).pipe(
            map(([formSelectOptions, pureFilter]: [FormSelectOption[], MultiSelectFilter]) => ({
                isLiveData: pureFilter.isLiveSearch,
                options: formSelectOptions
            }))
        );
    }

    private initPrefixStream(): void {
        this.prefix$ = this.pureFilterStream$.pipe(
            map(multiSelectFilter => {
                const sign: string = multiSelectFilter.strategy === QueryPartStrategy.MatchNone ? '-' : '';
                return `${sign}${multiSelectFilter.label}`;
            })
        );
    }

    private initIdStream(): void {
        this.id$ = this.pureFilterStream$.pipe(
            map(multiSelectFilter => `${multiSelectFilter.field}-filter-multiselect`)
        );
    }

    private initStyleStream(): void {
        this.style$ = this.pureFilterStream$.pipe(map(selectFilter => selectFilter.style));
    }

    private search$(term: string): Observable<FormSelectOption[]> {
        return of([]).pipe(
            tap(() => (this.loading = true)),
            withLatestFrom(this.pureFilterStream$),
            switchMap(([_, pureFilter]) => pureFilter.searchFct(term).pipe(takeUntil(this.searchTerm$))),
            tap(() => (this.loading = false))
        );
    }

    public onSearchTermChange(term: string): void {
        this.searchTerm$.next(term);
    }
}
